export interface QuoteDetailItem {
  uniqueKey: string;
  quoteVerified: boolean;
  createdOn?: string | null;
  verifiedOn?: string | null;
  userSend?: number | null;
  recipientReceive?: number | null;
  sendCurrency?: string | null;
  receiveCurrency?: string | null;
  docPath?: string | null;
  docName?: string | null;
}

export interface QuoteDetailsState {
  list: QuoteDetailItem[];
  loading: boolean;
  error: string | null;
}

const hasTruthyDate = (value: unknown): boolean => {
  if (value == null || value === "") return false;
  if (typeof value === "string" && ["pending", "null", "undefined"].includes(value.toLowerCase())) {
    return false;
  }
  return true;
};

const normalizeVerifiedStatus = (item: any): boolean => {
  const verified =
    item?.quoteVerified ??
    item?.quote_verified ??
    item?.verified ??
    item?.isVerified ??
    item?.is_verified;

  if (typeof verified === "boolean") return verified;
  if (typeof verified === "number") return verified === 1;
  if (typeof verified === "string") {
    const value = verified.trim().toLowerCase();
    if (["true", "verified", "yes", "1"].includes(value)) return true;
    if (["false", "pending", "no", "0"].includes(value)) return false;
  }

  const status = String(
    item?.status ??
      item?.quoteStatus ??
      item?.verificationStatus ??
      item?.quote_status ??
      item?.verification_status ??
      ""
  )
    .trim()
    .toLowerCase();

  if (["verified", "complete", "completed", "success"].includes(status)) return true;
  if (["pending", "sent", "unverified", "in_progress"].includes(status)) return false;

  const verifiedDate =
    item?.verifiedOn ??
    item?.verified_on ??
    item?.verifiedDate ??
    item?.verified_date ??
    item?.quoteVerifiedDate ??
    item?.quote_verified_date;

  if (hasTruthyDate(verifiedDate)) return true;

  return false;
};

export const normalizeQuoteDetailItem = (item: any): QuoteDetailItem => ({
  uniqueKey:
    item?.uniqueKey ??
    item?.unique_key ??
    item?.id ??
    item?.quoteId ??
    item?.quote_id ??
    "",
  quoteVerified: normalizeVerifiedStatus(item),
  createdOn: item?.createdOn ?? null,
  userSend:
    item?.userSend ??
    item?.user_send ??
    item?.conversionAmount ??
    item?.conversion_amount ??
    null,
  recipientReceive:
    item?.recipientRecieve ??
    item?.recipientReceive ??
    item?.recipient_recieve ??
    item?.recipient_receive ??
    null,
  sendCurrency: item?.baseCurrency ?? item?.base_currency ?? item?.receiveCurrency ?? "INR",
  receiveCurrency: item?.destCurrency ?? item?.dest_currency ?? item?.sendCurrency ?? "USD",
  verifiedOn:
    item?.verifiedOn ??
    item?.verified_on ??
    item?.verifiedDate ??
    item?.verified_date ??
    item?.quoteVerifiedDate ??
    item?.quote_verified_date ??
    null,
  docPath: item?.docPath ?? item?.doc_path ?? null,
  docName: item?.docName ?? item?.doc_name ?? null,
});

const extractQuoteList = (data: any): any[] => {
  if (Array.isArray(data)) return data;

  const candidates = [
    data?.quotes,
    data?.quoteDetails,
    data?.quoteList,
    data?.quote_list,
    data?.list,
    data?.data,
    data?.result,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
    if (candidate?.quotes && Array.isArray(candidate.quotes)) return candidate.quotes;
    if (candidate?.list && Array.isArray(candidate.list)) return candidate.list;
  }

  return [];
};

export const normalizeQuoteDetailsResponse = (data: any): QuoteDetailItem[] => {
  return extractQuoteList(data)
    .map(normalizeQuoteDetailItem)
    .filter((item: QuoteDetailItem) => Boolean(item.uniqueKey));
};
